import { useState, useEffect, useReducer, useContext } from "react";
import toastError from "../../errors/toastError";

import api from "../../services/api";
import { SocketContext } from "../../context/Socket/SocketContext";
import { isWhatsappDeleted } from "../../helpers/whatsappStatus";

let whatsAppsInFlightRequest = null;
let whatsAppsCachedData = null;
let whatsAppsCacheAt = 0;
const WHATSAPPS_CACHE_TTL_MS = 15000;

const fetchWhatsAppsShared = async () => {
  const now = Date.now();
  if (whatsAppsCachedData && now - whatsAppsCacheAt < WHATSAPPS_CACHE_TTL_MS) {
    return whatsAppsCachedData;
  }

  if (whatsAppsInFlightRequest) {
    return whatsAppsInFlightRequest;
  }

  whatsAppsInFlightRequest = api
    .get("/whatsapp/?session=0", { timeout: 10000 })
    .then(({ data }) => {
      whatsAppsCachedData = data;
      whatsAppsCacheAt = Date.now();
      return data;
    })
    .finally(() => {
      whatsAppsInFlightRequest = null;
    });

  return whatsAppsInFlightRequest;
};

// Conexão excluída nunca pode ser selecionada: some da lista na hora e derruba o
// cache compartilhado, para o próximo modal não remontar com ela.
const invalidateWhatsAppsSharedCache = () => {
  whatsAppsCachedData = null;
  whatsAppsCacheAt = 0;
};

const withoutDeleted = (whatsApps) =>
  (Array.isArray(whatsApps) ? whatsApps : []).filter((w) => w && !isWhatsappDeleted(w));

const removeWhatsApp = (state, whatsAppId) =>
  state.filter((s) => Number(s.id) !== Number(whatsAppId));

const reducer = (state, action) => {
  if (action.type === "LOAD_WHATSAPPS") {
    return withoutDeleted(action.payload);
  }

  if (action.type === "UPDATE_WHATSAPPS") {
    const whatsApp = action.payload;
    if (!whatsApp || whatsApp.id == null) {
      return state;
    }
    if (isWhatsappDeleted(whatsApp)) {
      return removeWhatsApp(state, whatsApp.id);
    }
    const whatsAppIndex = state.findIndex((s) => s.id === whatsApp.id);

    if (whatsAppIndex !== -1) {
      state[whatsAppIndex] = whatsApp;
      return [...state];
    } else {
      return [whatsApp, ...state];
    }
  }

  if (action.type === "UPDATE_SESSION") {
    const whatsApp = action.payload;
    if (!whatsApp || whatsApp.id == null) {
      return state;
    }
    // Banimento: a sessão chega com status "deleted" — a conexão foi excluída.
    if (isWhatsappDeleted(whatsApp)) {
      return removeWhatsApp(state, whatsApp.id);
    }
    const whatsAppIndex = state.findIndex((s) => s.id === whatsApp.id);

    if (whatsAppIndex !== -1) {
      state[whatsAppIndex].status = whatsApp.status;
      state[whatsAppIndex].updatedAt = whatsApp.updatedAt;
      state[whatsAppIndex].qrcode = whatsApp.qrcode;
      state[whatsAppIndex].retries = whatsApp.retries;
      if (Object.prototype.hasOwnProperty.call(whatsApp, "session")) {
        state[whatsAppIndex].hasSession = !!whatsApp.session;
      }
      if (Object.prototype.hasOwnProperty.call(whatsApp, "hasSession")) {
        state[whatsAppIndex].hasSession = !!whatsApp.hasSession;
      }
      return [...state];
    } else {
      return [...state];
    }
  }

  if (action.type === "DELETE_WHATSAPPS") {
    return removeWhatsApp(state, action.payload);
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useWhatsApps = () => {
  const [whatsApps, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(true);

  const socketManager = useContext(SocketContext);

  useEffect(() => {
    setLoading(true);
    let cancelled = false;

    const fetchSession = async () => {
      try {
        // Verificar se há token antes de fazer a requisição
        const token = localStorage.getItem("token");
        if (!token) {
          if (!cancelled) {
            setLoading(false);
          }
          return;
        }

        const data = await fetchWhatsAppsShared();
        if (cancelled) {
          return;
        }

        dispatch({ type: "LOAD_WHATSAPPS", payload: data });
        setLoading(false);
      } catch (err) {
        if (cancelled) {
          return;
        }

        setLoading(false);
        toastError(err);
      }
    };
    fetchSession();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);

    socket.on(`company-${companyId}-whatsapp`, (data) => {
      if (data.action === "update") {
        if (isWhatsappDeleted(data.whatsapp)) invalidateWhatsAppsSharedCache();
        dispatch({ type: "UPDATE_WHATSAPPS", payload: data.whatsapp });
      }
    });

    socket.on(`company-${companyId}-whatsapp`, (data) => {
      if (data.action === "delete") {
        invalidateWhatsAppsSharedCache();
        dispatch({ type: "DELETE_WHATSAPPS", payload: data.whatsappId });
      }
    });

    socket.on(`company-${companyId}-whatsappSession`, (data) => {
      if (data.action === "update") {
        if (isWhatsappDeleted(data.session)) invalidateWhatsAppsSharedCache();
        dispatch({ type: "UPDATE_SESSION", payload: data.session });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [socketManager]);

  return { whatsApps, loading };
};

export default useWhatsApps;
